/**
 * Conversation mode for Milo on the web (KEY-850): click once, then talk — Milo answers out loud and listens again,
 * until you stop, go quiet for a minute, or something goes wrong.
 *
 * This file is the turn-taking only, as plain data, so every transition can be tested without a browser. The
 * microphone (Web Speech recognition) and the speaker (speechSynthesis) live in `useMiloConversation`, which feeds
 * this events and does what the state says: listen only in `listening`, speak only in `speaking`.
 *
 *   off ─start─▶ listening ─(words, then 1.2s quiet)─▶ sending ─reply─▶ speaking ─spoken─▶ settling ─300ms─▶ listening
 *
 * Recognition is aborted outside `listening`, and `settling` holds it off for a moment after Milo finishes, so he
 * never hears himself. A browser with no recognition (Firefox) gets `read` mode instead: typed questions, replies
 * read aloud, `idle` ↔ `sending` ↔ `speaking`.
 */

export type ConversationPhase = "off" | "listening" | "sending" | "speaking" | "settling" | "idle";

/** `talk`: hands-free, both ways. `read`: replies read aloud to typed questions (no recognition in this browser). */
export type ConversationKind = "talk" | "read";

export interface ConversationSupport {
  recognition: boolean;
  synthesis: boolean;
}

export interface ConversationState {
  phase: ConversationPhase;
  kind: ConversationKind | null;
  support: ConversationSupport;
  /** Final words from recognition sessions that have already ended (Safari ends continuous mode on its own). */
  committed: string;
  /** Final words from the session running now. */
  sessionFinal: string;
  /** Words still being recognised — shown, never sent. */
  interim: string;
  /** When something was last heard (or listening began), for the silence rules. */
  lastHeardAt: number;
  /** A turn waiting to be sent: the hook sends it and answers with `taken`. */
  outgoing: string | null;
  /** What to say while `speaking`; `sayId` changes with every new thing to say. */
  say: string | null;
  sayId: number;
  /** A speech engine that never says it finished must not hold the mic off for ever. */
  speakDeadline: number;
  settleUntil: number;
  /** Turn the mode off once this reply has been said (the conversation is full). */
  endAfterSpeaking: boolean;
  /** Why the mode went off, when it wasn't the person's own doing. */
  notice: string | null;
}

export const SEND_AFTER_QUIET_MS = 1200;
/** Safari can leave everything interim; words with no final after this much quiet go anyway. */
export const SEND_INTERIM_AFTER_QUIET_MS = 2500;
export const SILENCE_OFF_MS = 60_000;
export const SETTLE_MS = 300;
export const MIN_TRANSCRIPT = 2;

export const NOTICE_SILENCE = "Stopped listening after a minute of quiet.";
export const NOTICE_FULL = "This conversation is full — start a new one to keep talking.";
export const NOTICE_MIC = "Milo needs the microphone. Allow it for this site (the icon in the address bar), then turn talking on again.";
export const NOTICE_NO_MIC = "No microphone was found. Plug one in, then turn talking on again.";
export const NOTICE_NETWORK = "Speech recognition needs a connection. Type instead, or try again in a moment.";
export const NOTICE_FAILED = "Milo couldn't answer, so talking is off. Turn it on again when you're ready.";

export type ConversationEvent =
  | { type: "start"; now: number }
  | { type: "stop"; notice?: string | null }
  /** Recognition results for the running session: all its final words, and what is still being heard. */
  | { type: "heard"; final: string; interim: string; now: number }
  /** A recognition session ended (on its own or aborted); its final words are kept. */
  | { type: "recognitionEnded" }
  | { type: "recognitionError"; error: string }
  | { type: "tick"; now: number }
  /** The hook has sent `outgoing`. */
  | { type: "taken" }
  /** A turn went by another route (typed, or a Do it / Not now button). */
  | { type: "sending" }
  | { type: "reply"; text: string; turnsLeft: number | null; proposed: boolean; now: number }
  | { type: "failed" }
  | { type: "spoken"; now: number }
  /** The person started typing: stop talking over them. */
  | { type: "typing"; now: number };

export function initialConversation(support: ConversationSupport): ConversationState {
  return {
    phase: "off",
    kind: null,
    support,
    committed: "",
    sessionFinal: "",
    interim: "",
    lastHeardAt: 0,
    outgoing: null,
    say: null,
    sayId: 0,
    speakDeadline: 0,
    settleUntil: 0,
    endAfterSpeaking: false,
    notice: null,
  };
}

/** What this browser can do: hands-free, read-aloud only, or nothing (no toggle). */
export function conversationKind(support: ConversationSupport): ConversationKind | null {
  if (support.recognition) return "talk";
  if (support.synthesis) return "read";
  return null;
}

/** Whether the microphone should be running. The hook listens exactly when this is true — never while speaking. */
export function recognitionRuns(state: ConversationState): boolean {
  return state.kind === "talk" && state.phase === "listening";
}

/** Everything heard so far in this listening stretch. */
export function heardText(state: ConversationState): string {
  return join(state.committed, state.sessionFinal);
}

function join(a: string, b: string): string {
  return [a.trim(), b.trim()].filter(Boolean).join(" ");
}

function cleared(state: ConversationState): ConversationState {
  return { ...state, committed: "", sessionFinal: "", interim: "" };
}

function off(state: ConversationState, notice: string | null): ConversationState {
  return { ...cleared(initialConversation(state.support)), sayId: state.sayId, notice };
}

/** Back to waiting for the person: listening again (talk) or waiting for typing (read). */
function resume(state: ConversationState, now: number): ConversationState {
  return { ...cleared(state), phase: state.kind === "talk" ? "listening" : "idle", lastHeardAt: now, say: null };
}

/** Speech engines run at roughly 15 characters a second; allow double, plus a margin, before giving up on `end`. */
function deadline(text: string, now: number): number {
  return now + 5000 + text.length * 130;
}

export function conversationStep(state: ConversationState, event: ConversationEvent): ConversationState {
  switch (event.type) {
    case "start": {
      const kind = conversationKind(state.support);
      if (!kind) return state;
      return { ...cleared(state), kind, phase: kind === "talk" ? "listening" : "idle", lastHeardAt: event.now, outgoing: null, say: null, endAfterSpeaking: false, notice: null };
    }

    case "stop":
      return off(state, event.notice ?? null);

    case "heard":
      if (state.phase !== "listening") return state;
      return { ...state, sessionFinal: event.final, interim: event.interim, lastHeardAt: event.now };

    case "recognitionEnded":
      if (state.phase !== "listening") return state;
      return { ...state, committed: heardText(state), sessionFinal: "", interim: "" };

    case "recognitionError":
      switch (event.error) {
        case "not-allowed":
        case "service-not-allowed":
          return state.kind ? off(state, NOTICE_MIC) : state;
        case "audio-capture":
          return state.kind ? off(state, NOTICE_NO_MIC) : state;
        case "network":
          return state.kind ? off(state, NOTICE_NETWORK) : state;
        default:
          // "no-speech", "aborted" and the rest: the session ends, and the hook starts another while listening.
          return state;
      }

    case "tick": {
      const now = event.now;
      if (state.phase === "listening") {
        const heard = heardText(state);
        const quiet = now - state.lastHeardAt;
        const ready = heard ? quiet >= SEND_AFTER_QUIET_MS : !!state.interim.trim() && quiet >= SEND_INTERIM_AFTER_QUIET_MS;
        if (ready) {
          const said = join(heard, state.interim);
          // A cough or a stray syllable is not a question.
          if (said.length < MIN_TRANSCRIPT) return cleared(state);
          return { ...cleared(state), phase: "sending", outgoing: said };
        }
        if (!heard && !state.interim && quiet >= SILENCE_OFF_MS) return off(state, NOTICE_SILENCE);
        return state;
      }
      if (state.phase === "settling" && now >= state.settleUntil) return resume(state, now);
      if (state.phase === "speaking" && now >= state.speakDeadline) return afterSpeaking(state, now);
      return state;
    }

    case "taken":
      return { ...state, outgoing: null };

    case "sending":
      if (!state.kind) return state;
      return { ...cleared(state), phase: "sending", say: null };

    case "reply": {
      if (!state.kind) return state;
      const full = event.turnsLeft === 0;
      const words = spokenReply(event.text, event.proposed);
      if (!state.support.synthesis || !words) {
        return full ? off(state, NOTICE_FULL) : resume(state, event.now);
      }
      return {
        ...cleared(state),
        phase: "speaking",
        say: words,
        sayId: state.sayId + 1,
        speakDeadline: deadline(words, event.now),
        endAfterSpeaking: full,
        outgoing: null,
      };
    }

    case "failed":
      // A refusal (the pool is spent) or an error: talking on would only be refused again.
      return state.kind ? off(state, NOTICE_FAILED) : state;

    case "spoken":
      if (state.phase !== "speaking") return state;
      return afterSpeaking(state, event.now);

    case "typing":
      if (state.phase !== "speaking" && state.phase !== "settling") return state;
      return resume(state, event.now);
  }
}

function afterSpeaking(state: ConversationState, now: number): ConversationState {
  if (state.endAfterSpeaking) return off(state, NOTICE_FULL);
  if (state.kind === "talk") return { ...state, phase: "settling", say: null, settleUntil: now + SETTLE_MS };
  return resume(state, now);
}

/**
 * What Milo says out loud for a reply. A proposed write still shows its card; the voice adds "Shall I?" unless
 * the reply already asks, so a spoken "yes" (which the server's free parser understands) can answer it.
 */
export function spokenReply(text: string, proposed: boolean): string {
  const words = speakable(text);
  if (!proposed) return words;
  if (/\?\s*$/.test(words)) return words;
  return words ? `${words} Shall I?` : "Shall I?";
}

/**
 * Milo's reply as it should sound: the markdown he sometimes writes is read as symbols otherwise. Ported from
 * the phone (src/mobile/src/api/miloVoice.ts), with list items ending as sentences so they aren't run together.
 */
export function speakable(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]*)`/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s{0,3}#{1,6}\s+(.*)$/gm, (_, h: string) => endSentence(h))
    .replace(/^\s*(?:[-*•+]|\d+[.)])\s+(.*)$/gm, (_, item: string) => endSentence(item))
    .replace(/^\s*>\s?/gm, "")
    .replace(/(\*\*|__|\*|_|~~)(.+?)\1/g, "$2")
    .replace(/\s+/g, " ")
    .trim();
}

function endSentence(line: string): string {
  const t = line.trim();
  return /[.!?:;,]$/.test(t) ? t : `${t}.`;
}
